import { useCallback, useMemo, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { RorosPage } from '@/components/roros/RorosPage';
import { QueryPage } from '@/components/QueryPage';
import { Card } from '@/components/ui/Card';
import { Progress } from '@/components/ui/Progress';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { SimpleTooltip } from '@/components/ui/Tooltip/Tooltip';
import { ResearchConfirmDialog } from '@/components/ui/ResearchConfirmDialog';
import { MarketTree } from '@/components/roros/MarketTree';
import { CompanyDataPanel } from './CompanyDataPanel';
import {
  useRorosOverarchingCompletion,
  useRorosCompanyProfile,
  useAiProviderConfigured,
  useTriggerCompanyProfileResearch,
} from '@/hooks/roros/overarching';
import { useSocketEvent } from '@/hooks/useSocket';
import type { CompanyProfileResponse } from '@/types/roros/overarching';
import { Building2, BrainCircuit, Loader2 } from 'lucide-react';
import { t } from '@/lib/i18n';
import { usePermissions } from '@/hooks/usePermissions';
import { PERMISSIONS } from '@/constants/permissions';
import { OverarchingEditableProvider } from './OverarchingEditableContext';
import {
  ProfileFieldsSection,
  SingleFieldSection,
  purposeVisionFields,
  strategyFields,
} from './sections/ProfileFieldsSection';
import type { ProfileFieldDef } from './sections/ProfileFieldsSection';
import { CoreValuesSection } from './sections/CoreValuesSection';
import { PrinciplesSection } from './sections/PrinciplesSection';
import { UspsSection } from './sections/UspsSection';
import { SwtSection } from './sections/SwtSection';
import { BusinessModelCanvas } from './canvas/BusinessModelCanvas';
import { ValuePropositionCanvas } from './canvas/ValuePropositionCanvas';

// ─── Dashboard ───────────────────────────────────────────────────────────────

export function OverarchingDashboard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') ?? 'company';

  const [showResearchDialog, setShowResearchDialog] = useState(false);
  const [isResearching, setIsResearching] = useState(false);

  const completionQuery = useRorosOverarchingCompletion();
  const profileQuery = useRorosCompanyProfile();
  const aiProviderQuery = useAiProviderConfigured();
  const triggerResearch = useTriggerCompanyProfileResearch();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission(PERMISSIONS.ROROS_OVERARCHING_MANAGE);

  const isAiProviderConfigured = aiProviderQuery.data ?? false;

  useSocketEvent('roros.company-profile.research-started', useCallback(() => setIsResearching(true), []));
  useSocketEvent('roros.company-profile.research-completed', useCallback(() => setIsResearching(false), []));
  useSocketEvent('roros.company-profile.research-failed', useCallback(() => setIsResearching(false), []));

  function handleTabChange(tab: string) {
    setSearchParams({ tab });
  }

  function handleResearchConfirm(overwriteMode: 'fill_only' | 'overwrite') {
    setShowResearchDialog(false);
    setIsResearching(true);
    triggerResearch.mutate(overwriteMode, { onError: () => setIsResearching(false) });
  }

  const purposeVisionDefs = useMemo(() => purposeVisionFields(), []);
  const strategyDefs = useMemo(() => strategyFields(), []);

  const industryField: ProfileFieldDef = {
    name: 'industry',
    label: t('Industry'),
    type: 'text',
    placeholder: t('e.g. Technology, Healthcare'),
    hint: t('What industry does your company operate in?'),
  };

  const productsServicesField: ProfileFieldDef = {
    name: 'productsServices',
    label: t('Products & Services'),
    type: 'textarea',
    placeholder: t('What do you offer?'),
    hint: t('What do you sell? Describe the experience and outcome, not just features.'),
  };

  const valuePropositionField: ProfileFieldDef = {
    name: 'valueProposition',
    label: t('Value Proposition'),
    type: 'textarea',
    placeholder: t('What unique value do you deliver?'),
    hint: t('What specific value do you deliver? What need do you satisfy, and how?'),
  };

  const brandPromiseField: ProfileFieldDef = {
    name: 'brandPromise',
    label: t('Brand Promise'),
    type: 'textarea',
    placeholder: t('What do you promise your customers?'),
    hint: t('What do you promise your customers? How would they verify you kept it? What happens if you don\'t?'),
  };

  return (
    <OverarchingEditableProvider canEdit={canEdit}>
    <RorosPage
      title={t('Company Settings')}
      icon={<Building2 className="h-6 w-6" />}
    >
      <ResearchConfirmDialog
        isOpen={showResearchDialog}
        onFillOnly={() => handleResearchConfirm('fill_only')}
        onOverwrite={() => handleResearchConfirm('overwrite')}
        onCancel={() => setShowResearchDialog(false)}
        isLoading={triggerResearch.isPending}
      />

      {/* Completion progress */}
      <div className="mb-6">
        <QueryPage query={completionQuery}>
          {(data) => {
            const pct = data.percentage ?? 0;
            return (
              <Card padding="md">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-foreground">{t('Overall Completion')}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-primary-600">{Math.round(pct)}%</span>
                    {canEdit && (isAiProviderConfigured ? (
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowResearchDialog(true)}
                        disabled={isResearching || triggerResearch.isPending}
                        data-testid="research-with-ai-button"
                        aria-label={t('Research with AI')}
                      >
                        {isResearching || triggerResearch.isPending ? (
                          <>
                            <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                            {t('Researching...')}
                          </>
                        ) : (
                          <>
                            <BrainCircuit className="h-4 w-4 mr-1.5" />
                            {t('Research with AI')}
                          </>
                        )}
                      </Button>
                    ) : (
                      <SimpleTooltip
                        content={t('Configure an AI provider in System Settings first')}
                        side="left"
                      >
                        <span className="inline-flex">
                          <Button variant="secondary" size="sm" disabled data-testid="research-with-ai-button-disabled" aria-label={t('Research with AI')}>
                            <BrainCircuit className="h-4 w-4 mr-1.5" />
                            {t('Research with AI')}
                          </Button>
                        </span>
                      </SimpleTooltip>
                    ))}
                  </div>
                </div>
                <Progress value={pct} max={100} size="md" variant={pct >= 80 ? 'success' : pct >= 40 ? 'warning' : 'default'} />
                {data.tabs && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('Company')}: {data.tabs.company.percentage}% ·{' '}
                    {t('Strategy')}: {data.tabs.strategy.percentage}% ·{' '}
                    {t('Market')}: {data.tabs.market.percentage}%
                  </p>
                )}
              </Card>
            );
          }}
        </QueryPage>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="mb-4">
          <TabsTrigger value="company">{t('Company')}</TabsTrigger>
          <TabsTrigger value="strategy">{t('Strategy')}</TabsTrigger>
          <TabsTrigger value="market">{t('Market')}</TabsTrigger>
          <TabsTrigger value="business-model">{t('Business Model')}</TabsTrigger>
          <TabsTrigger value="value-proposition">{t('Value Proposition')}</TabsTrigger>
          <TabsTrigger value="data">{t('Data')}</TabsTrigger>
        </TabsList>

        {/* Data tab reads from the Zustand org store, not from profileQuery,
            so it renders OUTSIDE the QueryPage wrapper. That way it stays
            usable even if the company-profile query is loading or errors. */}
        <TabsContent value="data">
          <CompanyDataPanel />
        </TabsContent>

        <TabsContent value="business-model">
          <BusinessModelCanvas canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="value-proposition">
          <ValuePropositionCanvas canEdit={canEdit} />
        </TabsContent>

        <QueryPage query={profileQuery}>
          {(data: CompanyProfileResponse) => {
            const profile = data.profile;
            if (!profile) {
              return <p className="text-muted-foreground">{t('No profile data available.')}</p>;
            }
            return (
              <>
                <TabsContent value="company">
                  <div className="space-y-6">
                    <ProfileFieldsSection
                      title={t('Purpose & Vision')}
                      profile={profile}
                      fields={purposeVisionDefs}
                    />
                    <div className="rounded-md border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
                      {t('Vision Goals are now Outcomes anchored to Vision. Open Outcomes →')}
                      {' '}
                      <Link to="/roros/artifacts/outcomes" className="underline font-medium">
                        {t('Open Outcomes →')}
                      </Link>
                    </div>
                    <SingleFieldSection title={t('Industry')} profile={profile} field={industryField} />
                    <SingleFieldSection title={t('Products & Services')} profile={profile} field={productsServicesField} />
                    <CoreValuesSection />
                    <PrinciplesSection />
                  </div>
                </TabsContent>

                <TabsContent value="strategy">
                  <div className="space-y-6">
                    <ProfileFieldsSection title={t('Strategy')} profile={profile} fields={strategyDefs} />
                    <SwtSection
                      title={t('Strengths')}
                      emptyHint={t('No strengths listed yet.')}
                      swtType="strength"
                    />
                    <SwtSection
                      title={t('Weaknesses')}
                      emptyHint={t('No weaknesses listed yet.')}
                      swtType="weakness"
                    />
                    <SwtSection
                      title={t('Trends')}
                      emptyHint={t('No trends listed yet.')}
                      swtType="trend"
                    />
                  </div>
                </TabsContent>

                <TabsContent value="market">
                  <div className="space-y-6">
                    <SingleFieldSection title={t('Value Proposition')} profile={profile} field={valuePropositionField} />
                    <SingleFieldSection title={t('Brand Promise')} profile={profile} field={brandPromiseField} />
                    <UspsSection />
                    <Card padding="md">
                      <MarketTree />
                    </Card>
                  </div>
                </TabsContent>
              </>
            );
          }}
        </QueryPage>
      </Tabs>
    </RorosPage>
    </OverarchingEditableProvider>
  );
}
